import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useAppData } from "../../context/AppDataContext";
import TaskTable from "./TaskTable";
import TaskAssignments from "./TaskAssignments";
import TimeTrackingButton from "../forms/TimeTrackingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faListCheck,
  faCalendarDays,
  faChevronRight,
  faUpRightAndDownLeftFromCenter,
} from "@fortawesome/free-solid-svg-icons";
import FormFieldTags from "../input/FormFieldTags";
import { errorToast, successfulToast } from "../../util/toastNotifications";
import useClickOutside from "../../hooks/useClickOutside";
import { parseDescriptionForEditor } from "../../utils/parseDescriptionForEditor";

import ProjectDropdown from "../dropdown/ProjectDropdown";
import StatusDropdown from "../dropdown/StatusDropdown";
import PriorityDropdown from "../dropdown/PriorityDropdown";
import PointsDropdown from "../dropdown/PointsDropdown";
import useProjectChange from "../../hooks/task/useProjectChange";
import usePriorityChange from "../../hooks/task/usePriorityChange";
import usePointsChange from "../../hooks/task/usePointsChange";
import useDueDateChange from "../../hooks/task/useDueDateChange";
import { fetchProjectsForSelect } from "../../util/fetchProjectsForSelect";
import { getDownloadUrl } from "../../util/fileUrls";
import { handleFileDownload } from "../../util/downloadFile";
import NovelEditor from "../core/NovelEditor";
import { useTimeTracking } from "../../context/TimeTrackingContext";
import useDescriptionBlur from "../../hooks/task/useDescriptionBlur";
import ConfirmDelete from "../../components/modals/ConfirmDelete";
import CommentsActivityLogs from "./CommentsActivityLogs";
import TaskDependencies from "./TaskDependencies";
import { appendBreadcrumbSearch } from "../../helpers/breadcrumbNavigation";
import NavigationLink from "../navigation/NavigationLink";
import FilePreview from "../files/FilePreview";
import DragDrop from "../DragDrop";
import Loading from "../layout/Loading";

const TaskDetailDrawer = ({ taskId, isOpen, onClose, breadcrumbParent }) => {
  const history = useHistory();
  const { api } = useAppData();
  const { handleDataEntry } = useTimeTracking();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const dropdownRef = useClickOutside(() => setIsDropdownOpen(false));
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showPointsDropdown, setShowPointsDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [description, setDescription] = useState(null);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const originalDescriptionRef = useRef(null);
  const dateInputRef = useRef(null);

  useEffect(() => {
    if (taskId && isOpen) {
      handleDataEntry("taskId", taskId);

      const fetchTaskData = async () => {
        try {
          setLoading(true);
          const [taskData, statusesData] = await Promise.all([
            api.task.getTaskById(taskId),
            api.taskStatus.getTaskStatuses(),
          ]);
          setTask(taskData);
          setSubtasks(taskData.subtasks || []);
          setTaskStatuses(statusesData);

          const normalized = (taskData.files ?? []).map((f) => ({
            ...f,
            preview_url: f.preview_url ?? f.file_url ?? null,
          }));
          setUploadedFiles(normalized);

          const { doc, storedString } = parseDescriptionForEditor(
            taskData?.description
          );
          setDescription(doc);
          originalDescriptionRef.current =
            storedString ?? (doc ? JSON.stringify(doc) : null);

          const taskProjectId =
            taskData?.project_id ??
            taskData?.project?.project_id ??
            taskData?.project?.id ??
            null;

          const projectsData = await fetchProjectsForSelect(api, {
            currentProjectId: taskProjectId,
          });
          setProjects(projectsData);
          setShowStatusDropdown(false);
          setShowProjectDropdown(false);
          setShowPointsDropdown(false);
          setShowPriorityDropdown(false);
          setIsSavingDescription(false);
        } catch (error) {
          console.error("Error fetching task data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchTaskData();
    }
  }, [api, taskId, isOpen, handleDataEntry]);

  const hasAssignee = task?.assignments.length > 0 === true;

  const saveDescription = async () => {
    if (description === null && !originalDescriptionRef.current) {
      return;
    }

    const descriptionString = JSON.stringify(description);
    if (descriptionString === originalDescriptionRef.current) {
      return;
    }

    try {
      setIsSavingDescription(true);
      await api.task.updateTask(taskId, {
        description: descriptionString,
        version: task.version,
      });
      const updatedTask = await api.task.getTaskById(taskId);
      setTask(updatedTask);
      originalDescriptionRef.current = descriptionString;
    } catch (error) {
      if (error && error.status === 409) {
        errorToast(
          "This task has been updated. Copy your changes, close and reopen the drawer, and try again."
        );
      } else {
        errorToast("Failed to update task description");
      }
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleTagRemove = async (tagId, tag) => {
    try {
      const tagName = (tag?.name || "").toLowerCase();
      if (tagName === "blocked" || tagName === "blocking") {
        const dependencyTaskIds = (task?.dependencies || [])
          .map((dep) => dep?.task_id)
          .filter(Boolean);
        const dependantTaskIds = (task?.dependants || [])
          .map((dep) => dep?.task_id)
          .filter(Boolean);

        await api.task.updateTaskDependencies(taskId, {
          dependency_task_ids: tagName === "blocked" ? [] : dependencyTaskIds,
          dependant_task_ids: tagName === "blocking" ? [] : dependantTaskIds,
        });
        const refreshedTask = await api.task.getTaskById(taskId);
        setTask(refreshedTask);
        return;
      }

      const updated = await api.task.toggleTag(taskId, tagId);
      setTask((prev) => ({
        ...prev,
        tags: updated?.tags ?? prev.tags ?? [],
        ...(updated?.version != null ? { version: updated.version } : {}),
      }));
    } catch (error) {
      errorToast(error?.message || "Failed to remove tag from task");
    }
  };

  const handleAddTagToObject = async (tagId) => {
    try {
      const updated = await api.task.toggleTag(taskId, tagId);
      setTask((prev) => ({
        ...prev,
        tags: updated?.tags ?? prev.tags ?? [],
        ...(updated?.version != null ? { version: updated.version } : {}),
      }));
    } catch (error) {
      errorToast(error?.message || "Failed to add tag to task");
    }
  };

  const handleDrawerImageUpload = async ({ file, preview_url }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("task_id", taskId);
    formData.append("org_id", task.org_id);

    try {
      const response = await api.file.createFiles(formData);
      successfulToast(`${file.name} uploaded successfully!`);
    } catch (err) {
      errorToast(`Failed to upload "${file.name}". Please try again.`);
    }
  };

  const handleStatusChange = async (statusId) => {
    try {
      await api.task.updateTask(taskId, {
        status_id: statusId,
        version: task.version,
      });
      const updatedTask = await api.task.getTaskById(taskId);
      setTask(updatedTask);
      setShowStatusDropdown(false);
    } catch (error) {
      console.error("Error updating task status:", error);
      errorToast("Failed to update task status");
    }
  };

  const handleAssignmentsChange = async () => {
    const updatedTask = await api.task.getTaskById(taskId);
    setTask(updatedTask);
  };

  const handleEditTask = () => {
    setIsDropdownOpen(false);
    onClose();
    history.push(`/task-form/${taskId}`);
  };

  const closeAllDropdowns = () => {
    setShowProjectDropdown(false);
    setShowStatusDropdown(false);
    setShowPointsDropdown(false);
    setShowPriorityDropdown(false);
  };

  const handleProjectToggle = () => {
    const next = !showProjectDropdown;
    closeAllDropdowns();
    setShowProjectDropdown(next);
  };

  const handleStatusToggle = () => {
    const next = !showStatusDropdown;
    closeAllDropdowns();
    setShowStatusDropdown(next);
  };

  const handlePointsToggle = () => {
    const next = !showPointsDropdown;
    closeAllDropdowns();
    setShowPointsDropdown(next);
  };

  const handlePriorityToggle = () => {
    const next = !showPriorityDropdown;
    closeAllDropdowns();
    setShowPriorityDropdown(next);
  };

  const handleProjectChange = useProjectChange({
    api,
    taskId,
    task,
    setTask,
    setShowProjectDropdown,
  });

  const handlePriorityChange = usePriorityChange({
    api,
    taskId,
    task,
    setTask,
    setShowPriorityDropdown,
  });

  const handlePointsChange = usePointsChange({
    api,
    taskId,
    task,
    setTask,
    setShowPointsDropdown,
  });

  const handleDueDateChange = useDueDateChange({
    api,
    taskId,
    task,
    setTask,
  });

  if (!isOpen) return null;

  const openPreview = (file) => setPreviewFile(file);
  const closePreview = () => {
    if (previewFile?.preview_url?.startsWith("blob:")) {
      URL.revokeObjectURL(previewFile.preview_url);
    }
    setPreviewFile(null);
  };

  const handleTaskFileUpload = async ({ file, preview_url }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("task_id", taskId);
    formData.append("org_id", task?.org_id);

    try {
      const response = await api.file.createFiles(formData);
      const uploadedFile = response.results;
      if (!uploadedFile?.file_id) {
        const error = new Error("File upload failed");
        error.status = 400;
        throw error;
      }
      const newFile = { ...uploadedFile, preview_url };
      setUploadedFiles((prev) => [...prev, newFile]);
      setTask((prev) => ({ ...prev, files: [...(prev.files ?? []), newFile] }));
      successfulToast(`${file.name} uploaded successfully!`);
    } catch (err) {
      if (err?.status === 400) {
        errorToast(
          `"${file.name}" could not be uploaded. The file type may not be supported.`
        );
      } else {
        errorToast(`Failed to upload "${file.name}". Please try again.`);
      }
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await api.task.toggleFile(taskId, file.file_id);
      const updatedFiles = uploadedFiles.filter(
        (f) => f.file_id !== file.file_id
      );
      setUploadedFiles(updatedFiles);
      setTask((prev) => ({ ...prev, files: updatedFiles }));
      if (file.preview_url?.startsWith("blob:"))
        URL.revokeObjectURL(file.preview_url);
      successfulToast("File removed successfully");
    } catch (err) {
      errorToast("Failed to remove file");
    }
  };

  const onFileDownload = async (event, file) => {
    try {
      await handleFileDownload(event, file);
    } catch {
      errorToast(`Failed to download "${file.file_name}"`);
    }
  };

  if (loading) {
    return (
      <div className="task-drawer-overlay">
        <div className="task-drawer">
          <Loading />
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="task-drawer-overlay">
        <div className="task-drawer">
          <div>Task not found</div>
        </div>
      </div>
    );
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const currentStatusId =
    task.status_id ||
    task.status?.task_status_id ||
    task.status?.status_id ||
    null;
  const currentProjectId =
    task?.project_id ?? task?.project?.project_id ?? task?.project?.id ?? null;
  const currentProjectName = task?.project?.name ?? null;
  const currentStatus = currentStatusId
    ? taskStatuses.find((status) => status.task_status_id === currentStatusId)
    : null;
  const statusDisplayName =
    typeof task.status === "object" ? task.status?.name : task.status;
  const statusColor = task.status_color || currentStatus?.color;
  const currentPriorityValue = task.priority
    ? task.priority.toString().toLowerCase()
    : "low";
  const currentPointsValue =
    task.points_estimate !== undefined && task.points_estimate !== null
      ? Number(task.points_estimate)
      : 0;
  const dueDateValue = task.due_date
    ? (() => {
        const date = new Date(task.due_date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      })()
    : "";

  return (
    <div className="task-drawer-overlay" onClick={handleOverlayClick}>
      <div className="task-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="task-drawer-header">
          <div className="task-drawer-title">
            <FontAwesomeIcon icon={faListCheck} className="mr-2" />
            {task.name}
          </div>
          <div className="task-drawer-actions">
            <NavigationLink
              to={appendBreadcrumbSearch(`/task/${taskId}`, breadcrumbParent)}
              icon={faUpRightAndDownLeftFromCenter}
              className="full-size-btn"
              title="Open in full page"
              onClick={onClose}
            />
            <button
              className="task-drawer-action-btn"
              onClick={onClose}
              title="Close drawer"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <div className="task-drawer-content">
          <div className="detail-header">
            <div className="header-main ">
              <div className="header-tags">
                <FormFieldTags
                  onClick={(e) => e.stopPropagation()}
                  selectedTags={task.tags || []}
                  handleTagRemove={handleTagRemove}
                  handleAddTagToObject={handleAddTagToObject}
                  objectType="tasks"
                  objectId={taskId}
                  objectPk="task_id"
                  hideLabel={true}
                />
              </div>

              <div className="action-buttons mt-0" ref={dropdownRef}>
                <FontAwesomeIcon
                  icon={faEllipsisV}
                  className="dropdown-toggle"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
                {isDropdownOpen && (
                  <div
                    className="dropdown-menu show"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="mr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTask();
                      }}
                    >
                      Edit Task
                    </button>
                    <ConfirmDelete
                      objectType="task"
                      id={taskId}
                      isTaskDrawerOpen={isOpen}
                      closeTaskDrawer={onClose}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="header-info">
              <div className="info-item">
                <span className="info-label">Project:</span>
                <span className="info-value">
                  <ProjectDropdown
                    projects={projects}
                    currentProjectId={currentProjectId}
                    currentProjectName={currentProjectName}
                    showDropdown={showProjectDropdown}
                    onToggle={handleProjectToggle}
                    onClose={() => setShowProjectDropdown(false)}
                    onSelect={handleProjectChange}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className="info-value">
                  <StatusDropdown
                    statuses={taskStatuses}
                    currentStatusId={currentStatusId}
                    statusLabel={statusDisplayName}
                    statusColor={statusColor}
                    showDropdown={showStatusDropdown}
                    onToggle={handleStatusToggle}
                    onClose={() => setShowStatusDropdown(false)}
                    onSelect={handleStatusChange}
                    assignee={hasAssignee}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Priority:</span>
                <span className="info-value">
                  <PriorityDropdown
                    currentPriorityValue={currentPriorityValue}
                    showDropdown={showPriorityDropdown}
                    onToggle={handlePriorityToggle}
                    onClose={() => setShowPriorityDropdown(false)}
                    onSelect={handlePriorityChange}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Assigned To</span>
                <span className="info-value">
                  <TaskAssignments
                    taskId={taskId}
                    assignments={task.assignments || []}
                    onAssignmentsChange={handleAssignmentsChange}
                    layout="vertical"
                    task={task}
                    projects={projects}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Tracking Time</span>
                <span className="info-value">
                  <TimeTrackingButton
                    users={task.assignments}
                    taskId={taskId}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Points</span>
                <span className="info-value">
                  <PointsDropdown
                    currentPointsValue={currentPointsValue}
                    showDropdown={showPointsDropdown}
                    onToggle={handlePointsToggle}
                    onClose={() => setShowPointsDropdown(false)}
                    onSelect={handlePointsChange}
                  />
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Due Date:</span>
                <span className="info-value">
                  <div
                    className="date-input-wrapper"
                    onClick={() => dateInputRef.current?.showPicker?.()}
                  >
                    <FontAwesomeIcon
                      icon={faCalendarDays}
                      className="date-input-icon"
                    />
                    <span className="date-display-text">
                      {dueDateValue ? dueDateValue : "Select date"}
                    </span>
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={dueDateValue}
                      onChange={handleDueDateChange}
                      className="date-input-control"
                    />
                  </div>
                </span>
              </div>
              <TaskDependencies
                task={task}
                onTaskUpdated={(updatedTask) => {
                  setTask(updatedTask);
                }}
              />
            </div>
          </div>

          <div className="task-detail-split">
            <div className="task-detail-column task-detail-description info-item">
              <div className="info-label description-label">
                Description
                {isSavingDescription && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "0.875rem",
                      color: "#666",
                    }}
                  >
                    Saving...
                  </span>
                )}
              </div>
              <NovelEditor
                initialValue={description}
                onChange={setDescription}
                onBlur={saveDescription}
                onImageClick={({ src, alt }) =>
                  setPreviewFile({
                    file_type: "image/*",
                    file_url: src,
                    file_name: alt || "image",
                  })
                }
              />
              <DragDrop
                onSuccessfulUpload={handleTaskFileUpload}
                accept="*/*"
                multiple={true}
                maxSize={20_000_000}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
              />
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  <p className="file-upload-message">
                    File{uploadedFiles.length > 1 ? "s" : ""} uploaded
                    successfully:
                  </p>
                  <ul>
                    {uploadedFiles.map((file) => (
                      <li key={file.file_id} className="file-display-wrapper">
                        <span
                          className="file-name"
                          onClick={() => openPreview(file)}
                        >
                          {file.file_name}
                        </span>
                        <a
                          href={getDownloadUrl(file)}
                          className="download-link"
                          onClick={(e) => onFileDownload(e, file)}
                          title={`Download ${file.file_name}`}
                        >
                          ↓
                        </a>
                        <button
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(file);
                          }}
                        >
                          x
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <FilePreview file={previewFile} onClose={closePreview} />
            </div>

            <div className="task-detail-column task-detail-comments">
              <CommentsActivityLogs
                taskId={taskId}
                orgId={task.org_id}
                task={task}
              />
            </div>
          </div>

          <div className="flex-space-between mt-4">
            <h4>Subtasks</h4>
            <button
              className="btn btn-primary"
              onClick={() =>
                history.push(`/task-form?parent_task_id=${taskId}`)
              }
            >
              Add Subtask
            </button>
          </div>

          <div>
            <TaskTable
              data={subtasks}
              loading={loading}
              onDataChange={setSubtasks}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailDrawer;
